import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const HERO_LOOP_URL = 'https://gallery.chunilkor.co.kr/hero-loop.mp4';
const HERO_FULL_URL = 'https://gallery.chunilkor.co.kr/hero.mp4';

const Home: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullVideoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullOpen, setIsFullOpen] = useState(false);

  const toggleAudio = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    if (!next) v.play().catch(() => {});
    setIsMuted(next);
  };

  useEffect(() => {
    if (!isFullOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      if (fullVideoRef.current) fullVideoRef.current.pause();
    };
  }, [isFullOpen]);

  const modules = [
    {
      id: 'pick',
      name: 'PICK',
      description: '고객사 픽업 – 당일/정기 스케줄 운영',
      summary: '고객사 픽업(정기/수시)',
      customerBenefit: '리드타임 단축, 유연한 수거 운영',
      image: '/images/core/core_pick.png',
      color: '#3B82F6'
    },
    {
      id: 'pack',
      name: 'PACK',
      description: '상품화/라벨링/재포장/세트 구성',
      summary: '상품화/라벨링/재포장/키팅',
      customerBenefit: '불량·반려 감소, 채널별 스펙 대응',
      image: '/images/core/core_pack.png',
      color: '#10B981'
    },
    {
      id: 'transfer',
      name: 'TRANSFER',
      description: '코스트코 평택디포 환적 – 긴급/대량 처리',
      summary: '거점 환적/중간 상·하차 운영',
      customerBenefit: '예약·납품 동선 최적화, 긴급 대응',
      image: '/images/core/core_transfer.png',
      color: '#F59E0B'
    },
    {
      id: 'store',
      name: 'STORE',
      description: '보관/WMS 재고관리',
      summary: '보관/WMS 재고관리',
      customerBenefit: '재고 정확도 향상, 비용 예측 가능',
      image: '/images/core/core_store.png',
      color: '#8B5CF6'
    },
    {
      id: 'ship',
      name: 'SHIP',
      description: '전국 배송 및 유통센터 납품',
      summary: '출고/배송/납품 운영',
      customerBenefit: '전국 출고, 유통센터 납품 대응',
      image: '/images/core/core_ship.png',
      color: '#EF4444'
    },
    {
      id: 'import',
      name: 'IMPORT 3PL',
      description: '수입부터 출고까지 One-Stop',
      summary: '수입 물류 연계(입고~출고)',
      customerBenefit: '수입~유통 One-Stop 운영',
      image: '/images/core/core_import.png',
      color: '#06B6D4'
    }
  ];

  const packages = [
    {
      type: 'A-Type',
      name: '빠른 출고형',
      description: '긴급 출고·납기 대응 중심'
    },
    {
      type: 'B-Type',
      name: '상품화 중심형',
      description: '라벨링·상품화 작업량이 많은 고객'
    },
    {
      type: 'C-Type',
      name: '풀필먼트형',
      description: '온라인 브랜드 물류 아웃소싱'
    },
    {
      type: 'D-Type',
      name: '수입 3PL형',
      description: '수입 물류 연계부터 출고까지 통합 운영'
    }
  ];

  const strengths = [
    '필요 모듈만 선택하는 조합형 설계',
    '단일 창구 운영(보관·상품화·출고·배송·통관 지원 연계)',
    '인하우스 상품화 작업팀 운영',
    '평택권 유통거점 연계 환적 운영',
    '수입 물류 연계(IMPORT 3PL)까지 통합 운영',
    '표준화 프로세스 기반 비용 효율 구조',
    '중소기업·브랜드사 맞춤형 운영안 제안'
  ];

  return (
    <div className="home">
      {/* Hero Section — editorial fullscreen video (www3 concept) */}
      <section className="hero">
        <div className="hero-media" aria-hidden="true">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src={HERO_LOOP_URL} type="video/mp4" />
          </video>
          <div className="hero-gradient" />
        </div>

        <div className="hero-content">
          <div className="hero-eyebrow">
            <span>01 / Overview</span>
            <span className="hero-eyebrow-divider" />
            <span>Chunil · Package Service</span>
          </div>
          <h1 className="hero-title">코스트코 센터 납품대행</h1>
          <div className="hero-subtitle-group">
            <p className="hero-subtitle">조합형 원스톱 물류 서비스</p>
            <p className="hero-subtitle-lg">
              필요한 모듈만 선택해 고객 전용 운영안으로 설계합니다
            </p>
          </div>
          <div className="hero-cta">
            <Link to="/contact" className="btn-hero-primary">
              견적 요청하기
              <span aria-hidden className="arrow">→</span>
            </Link>
          </div>
        </div>

        {/* Video controls — Sound + Full Video */}
        <div className="hero-controls">
          <button
            type="button"
            onClick={toggleAudio}
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            aria-pressed={!isMuted}
            className="hero-control-btn"
          >
            {isMuted ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
                <line x1="22" x2="16" y1="9" y2="15" />
                <line x1="16" x2="22" y1="9" y2="15" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
                <path d="M16 9a5 5 0 0 1 0 6" />
                <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
              </svg>
            )}
            <span>{isMuted ? 'Sound' : 'Mute'}</span>
          </button>
          <button
            type="button"
            onClick={() => setIsFullOpen(true)}
            aria-label="Play full video"
            className="hero-control-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="6 3 20 12 6 21 6 3" />
            </svg>
            <span>Full Video</span>
          </button>
        </div>

        {/* Full video modal */}
        {isFullOpen && (
          <div
            className="video-modal"
            onClick={() => setIsFullOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Full company video"
          >
            <button
              type="button"
              onClick={() => setIsFullOpen(false)}
              aria-label="Close video"
              className="video-modal-close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <video
              ref={fullVideoRef}
              src={HERO_FULL_URL}
              controls
              autoPlay
              playsInline
              preload="auto"
              className="video-modal-video"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </section>

      {/* 6대 모듈 소개 */}
      <section className="modules-section">
        <div className="container">
          <h2 className="section-title">천일 6대 모듈</h2>
          <p className="section-brand">
            2PT2SI(PICK - PACK - TRANSFER - STORE - SHIP - IMPORT 3PL)
          </p>
          <p className="section-subtitle">
            천일 6대 모듈은 고객 수요에 따라 자유롭게 조합 가능한 통합 물류 서비스입니다.
            <br />
            고객사 화물을 픽업(PICK) 하고, 채널 스펙에 맞춘 상품화·라벨링(PACK) 을 수행하며,
            <br />
            평택권 유통거점 연계 환적(TRANSFER), 보관·재고관리(STORE), 전국 출고·배송(SHIP),
            <br />
            그리고 수입 물류 연계(IMPORT 3PL) 까지 하나의 흐름으로 운영합니다.
          </p>

          {/* 요약표 */}
          <div className="modules-summary-table">
            <div className="summary-table">
              <div className="table-header">
                <div className="table-col-module">모듈명</div>
                <div className="table-col-function">기능 요약</div>
                <div className="table-col-benefit">주요 고객효익</div>
              </div>
              {modules.map((module) => (
                <div key={module.id} className="table-row">
                  <div className="table-col-module">
                    <strong>{module.name}</strong>
                  </div>
                  <div className="table-col-function">{module.summary}</div>
                  <div className="table-col-benefit">{module.customerBenefit}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="modules-grid">
            {modules.map((module) => (
              <Link 
                key={module.id} 
                to={`/service#${module.id}`} 
                className="module-card"
              >
                <div className="module-image-wrapper">
                  <img
                    src={module.image}
                    alt={module.name}
                    className="module-image"
                    loading="lazy"
                  />
                  <span className="ai-label">AI생성</span>
                </div>
                <h3 className="module-name">{module.name}</h3>
                <p className="module-description">{module.description}</p>
              </Link>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/service" className="btn-editorial-outline">
              모듈 자세히 보기
              <span aria-hidden className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 조합형 패키지 소개 */}
      <section className="packages-section">
        <div className="container">
          <h2 className="section-title">조합형 패키지</h2>
          <p className="section-subtitle">
            천일의 6대 모듈을 필요 범위만 선택해 고객 전용 운영안으로 구성합니다.
          </p>
          <div className="packages-grid">
            {packages.map((pkg) => (
              <div key={pkg.type} className="package-card">
                <div className="package-type">{pkg.type}</div>
                <h3 className="package-name">{pkg.name}</h3>
                <p className="package-description">{pkg.description}</p>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/contact" className="btn-editorial-outline">
              견적 요청하기
              <span aria-hidden className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 입점·납품 스펙 최적화 */}
      <section className="spec-section">
        <div className="container">
          <div className="spec-content">
            <div className="spec-text">
              <h2 className="section-title">입점·납품 스펙 최적화</h2>
              <p className="section-subtitle">
                유통센터·PB/OEM 판매사를 위한 전문 물류 서비스
              </p>
              <ul className="spec-list">
                <li>유통센터 입고 스펙 충족율 개선</li>
                <li>라벨링/패킹 불량율 감소</li>
                <li>정시 출고율 목표: 98%+ (운영 KPI 기준)</li>
              </ul>
            </div>
            <div className="spec-image">
              <img
                src="/images/etc/spec_optimization.png"
                alt="팔레트/패킹룸/환적 장면"
                className="spec-image-content"
                loading="lazy"
              />
              <span className="ai-label">AI생성</span>
            </div>
          </div>
        </div>
      </section>

      {/* 천일의 강점 */}
      <section className="strengths-section">
        <div className="container">
          <h2 className="section-title">천일의 강점</h2>
          <div className="strengths-grid">
            {strengths.map((strength, index) => (
              <div key={index} className="strength-item">
                <p className="strength-text">{strength}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA — editorial asymmetric */}
      <section className="home-cta-section">
        <div className="container">
          <div className="cta-grid">
            <div className="cta-left">
              <div className="eyebrow light">06 / Contact</div>
              <h2 className="cta-title">
                맞춤형 물류 솔루션이 <span className="accent-amber">필요하신가요?</span><br />
                <span className="accent-white">전문 상담팀이 최적의 운영안을 제안합니다.</span>
              </h2>
              <p className="cta-description">
                SKU 수·일 출고량·필요 모듈에 맞춰 견적을 구성하고, 영업일 24시간 이내 회신드립니다.
              </p>
            </div>
            <div className="cta-right">
              <Link to="/contact" className="btn-editorial-light">
                견적 요청하기
                <span aria-hidden className="arrow">→</span>
              </Link>
              <p className="cta-note">상담 시간: 평일 10:00 – 18:00</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

